import type { ReactNode } from 'react'

/**
 * Иконки нарисованы здесь, а не подключены библиотекой: их полтора десятка,
 * а любая зависимость тянет в бандл сотни неиспользуемых.
 *
 * Рисунок следует SF Symbols: линия 1.8 при 24 единицах сетки, скруглённые
 * концы, силуэт узнаётся с расстояния вытянутой руки. В панели вкладок иконки
 * заполненные — так их показывает iOS.
 */

interface IconProps {
  size?: number
  className?: string
}

function Svg({ size = 22, className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

/** Сводка дня — три кольца активности, как в приложении «Фитнес». */
export function IconRings({ size = 22, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.4" />
      <circle cx="12" cy="12" r="5.6" stroke="currentColor" strokeWidth="2.4" strokeOpacity="0.75" />
      <circle cx="12" cy="12" r="2.2" stroke="currentColor" strokeWidth="2.4" strokeOpacity="0.5" />
    </svg>
  )
}

export function IconFood(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 3v8a2 2 0 0 0 4 0V3" />
      <path d="M8 11v10" />
      <path d="M17 3c-1.5 2-2 4-2 6.5 0 1.5.7 2.5 2 2.5V3z" />
      <path d="M17 12v9" />
    </Svg>
  )
}

export function IconGym(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 8v8" />
      <path d="M8 6v12" />
      <path d="M16 6v12" />
      <path d="M20 8v8" />
      <path d="M8 12h8" />
    </Svg>
  )
}

export function IconRuler(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 8h18v8H3z" />
      <path d="M7 8v4" />
      <path d="M11 8v6" />
      <path d="M15 8v4" />
      <path d="M19 8v6" />
    </Svg>
  )
}

export function IconHeart(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 20s-7.5-4.6-7.5-9.6A4.4 4.4 0 0 1 12 7.6a4.4 4.4 0 0 1 7.5 2.8C19.5 15.4 12 20 12 20z" />
    </Svg>
  )
}

export function IconPlus(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </Svg>
  )
}

export function IconMinus(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5 12h14" />
    </Svg>
  )
}

export function IconCheck(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 12.5 9.5 18 20 6.5" />
    </Svg>
  )
}

export function IconTrash(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 7h16" />
      <path d="M9 7V4h6v3" />
      <path d="M6 7v13h12V7" />
      <path d="M10 11v5" />
      <path d="M14 11v5" />
    </Svg>
  )
}

export function IconRefresh(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M20 11a8 8 0 1 0-.6 4" />
      <path d="M20 4v7h-7" />
    </Svg>
  )
}

export function IconChevronLeft(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M15 5l-7 7 7 7" />
    </Svg>
  )
}

export function IconChevronRight(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M9 5l7 7-7 7" />
    </Svg>
  )
}

export function IconX(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </Svg>
  )
}

export function IconFlask(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M10 3v6.5L4.5 18a2 2 0 0 0 1.7 3h11.6a2 2 0 0 0 1.7-3L14 9.5V3" />
      <path d="M9 3h6" />
      <path d="M7.5 15h9" />
    </Svg>
  )
}

export function IconExplain(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 3a6 6 0 0 0-3.5 10.9V17h7v-3.1A6 6 0 0 0 12 3z" />
      <path d="M9.5 20h5" />
    </Svg>
  )
}

export function IconWarning(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 4.5 21 19H3z" />
      <path d="M12 10v4" />
      <path d="M12 16.5h.01" />
    </Svg>
  )
}

export function IconCamera(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 8h3l1.5-2h7L17 8h3v12H4z" />
      <circle cx="12" cy="13.5" r="3.5" />
    </Svg>
  )
}
